import React, { useCallback, useEffect, useState } from 'react'
import { Box, Typography, Button, Chip, CircularProgress } from '@mui/material'
import { IconGift, IconExternalLink, IconCopy } from '@tabler/icons-react'
import { useAppDispatch } from '../../../../store'
import { setOpen, setMessage, setSeverity } from '../../../../store/profileSlice'
import {
  getRewardsInbox,
  regenerateClaimLink,
  RewardCard,
} from '../../../../services/walletPayoutService'
import { navigateTo } from '../../../../utils/navigation'
import { errorLog } from '../../../../utils/log-helper'
import { SANS_FONT } from '../../../../theme'

const formatUsd = (cents: number): string => `$${(cents / 100).toFixed(2)}`

// A reward is claimable when it's freshly redeemed/active. EXPIRED links can be re-minted on demand.
const isActionable = (status: string): boolean =>
  status === 'ACTIVE' || status === 'REDEEMED_EMAIL' || status === 'EXPIRED'

const statusChip = (
  status: string,
): { label: string; color: 'success' | 'default' | 'warning' } => {
  switch (status) {
    case 'USED':
      return { label: 'Claimed', color: 'default' }
    case 'EXPIRED':
      return { label: 'Link expired', color: 'warning' }
    default:
      return { label: 'Ready to claim', color: 'success' }
  }
}

const ProfileRewards: React.FC = () => {
  const dispatch = useAppDispatch()
  const [rewards, setRewards] = useState<RewardCard[] | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    try {
      const inbox = await getRewardsInbox()
      setRewards(inbox)
    } catch (error) {
      errorLog('[Rewards] Failed to load rewards inbox', error)
      setRewards([])
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const toast = (msg: string, severity: 'success' | 'error' | 'info'): void => {
    dispatch(setOpen(true))
    dispatch(setMessage(msg))
    dispatch(setSeverity(severity))
  }

  // Resolves a usable claim link, minting a fresh one when it's missing or expired (Tremendous
  // claim links lapse in ~1 day). Updates local state so subsequent taps reuse the fresh link.
  const resolveClaimUrl = async (card: RewardCard): Promise<string> => {
    let url = card.claimUrl ?? ''
    if (!url || card.status === 'EXPIRED') {
      url = await regenerateClaimLink(card.id)
      setRewards(
        (prev) =>
          prev?.map((r) => (r.id === card.id ? { ...r, claimUrl: url } : r)) ?? prev,
      )
    }
    return url
  }

  const handleClaim = async (card: RewardCard): Promise<void> => {
    setBusyId(card.id)
    try {
      const url = await resolveClaimUrl(card)
      if (url) {
        navigateTo(url)
      } else {
        toast('Could not open your reward. Please try again.', 'error')
      }
    } catch (error) {
      errorLog('[Rewards] Failed to open claim link', error)
      toast('Could not open your reward. Please try again.', 'error')
    } finally {
      setBusyId(null)
    }
  }

  const handleCopy = async (card: RewardCard): Promise<void> => {
    setBusyId(card.id)
    try {
      const url = await resolveClaimUrl(card)
      if (url) {
        await navigator.clipboard.writeText(url)
        toast('Claim link copied', 'success')
      } else {
        toast('Could not copy your reward link.', 'error')
      }
    } catch (error) {
      errorLog('[Rewards] Failed to copy claim link', error)
      toast('Could not copy your reward link.', 'error')
    } finally {
      setBusyId(null)
    }
  }

  if (rewards === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (rewards.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
        <IconGift size={40} stroke={1.5} />
        <Typography sx={{ fontFamily: SANS_FONT, fontWeight: 600, fontSize: 18, mt: 1.5 }}>
          No rewards yet
        </Typography>
        <Typography color="text.secondary" sx={{ fontFamily: SANS_FONT, mt: 0.5 }}>
          Redeem your monthly credit and your gift cards will show up here — ready to
          claim anytime, no email needed.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Typography color="text.secondary" sx={{ fontFamily: SANS_FONT, fontSize: 13 }}>
        Your redeemed gift cards. Claim any of them right here — no email needed.
      </Typography>
      {rewards.map((card) => {
        const chip = statusChip(card.status)
        const actionable = isActionable(card.status)
        const busy = busyId === card.id
        return (
          <Box
            key={card.id}
            sx={{
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography sx={{ fontFamily: SANS_FONT, fontWeight: 700, fontSize: 20 }}>
                  {formatUsd(card.amountCents)}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ fontFamily: SANS_FONT, fontSize: 12 }}
                >
                  {card.rewardCode ? `${card.rewardCode.replace(/_/g, ' ')} · ` : ''}
                  {card.created ? new Date(card.created).toLocaleDateString() : ''}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={chip.label}
                color={chip.color}
                variant={chip.color === 'default' ? 'outlined' : 'filled'}
              />
            </Box>
            {actionable && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disableElevation
                  disabled={busy}
                  onClick={() => {
                    void handleClaim(card)
                  }}
                  startIcon={
                    busy ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <IconExternalLink size={16} />
                    )
                  }
                  sx={{ textTransform: 'none', flex: 1 }}
                >
                  {card.status === 'EXPIRED' ? 'Get new link' : 'Claim'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={busy}
                  onClick={() => {
                    void handleCopy(card)
                  }}
                  startIcon={<IconCopy size={16} />}
                  sx={{ textTransform: 'none' }}
                >
                  Copy
                </Button>
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default ProfileRewards
